/**
 * React hooks for Negotiation Playbook API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  PlaybookSuggestionsResponse,
  ContextSuggestionsRequest,
  ObjectionMatrixResponse,
  PlaybookSearchResponse,
  PlaybookStats,
  PlaybookSuggestion,
  EntryType,
} from '../types/playbook';

const API_BASE = '/api/playbook';

/**
 * Fetch context-aware suggestions based on property/deal characteristics
 */
export function usePlaybookSuggestions(context: ContextSuggestionsRequest, enabled = true) {
  return useQuery<PlaybookSuggestionsResponse>({
    queryKey: ['playbook', 'suggestions', context],
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/suggestions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(context),
      });
      if (!response.ok) {
        throw new Error('Failed to fetch playbook suggestions');
      }
      return response.json();
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Search playbook entries with filters
 */
export function usePlaybookSearch(params: {
  query?: string;
  entry_types?: EntryType[];
  deal_contexts?: string[];
  seller_situations?: string[];
  page?: number;
  page_size?: number;
}) {
  const searchParams = new URLSearchParams();
  if (params.query) searchParams.set('query', params.query);
  if (params.entry_types?.length) {
    params.entry_types.forEach(t => searchParams.append('entry_types', t));
  }
  if (params.deal_contexts?.length) {
    params.deal_contexts.forEach(c => searchParams.append('deal_contexts', c));
  }
  if (params.seller_situations?.length) {
    params.seller_situations.forEach(s => searchParams.append('seller_situations', s));
  }
  if (params.page) searchParams.set('page', String(params.page));
  if (params.page_size) searchParams.set('page_size', String(params.page_size));

  return useQuery<PlaybookSearchResponse>({
    queryKey: ['playbook', 'search', params],
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/search?${searchParams.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to search playbook');
      }
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch objection handling matrix
 */
export function useObjectionMatrix(dealContext?: string) {
  const params = new URLSearchParams();
  if (dealContext) params.set('deal_context', dealContext);

  return useQuery<ObjectionMatrixResponse>({
    queryKey: ['playbook', 'objections', dealContext],
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/objections?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch objection matrix');
      }
      return response.json();
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Fetch playbook statistics
 */
export function usePlaybookStats() {
  return useQuery<PlaybookStats>({
    queryKey: ['playbook', 'stats'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/stats`);
      if (!response.ok) {
        throw new Error('Failed to fetch playbook stats');
      }
      return response.json();
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Fetch random suggestions for discovery
 */
export function useRandomSuggestions(entryType?: EntryType, limit = 5) {
  const params = new URLSearchParams();
  if (entryType) params.set('entry_type', entryType);
  params.set('limit', String(limit));

  return useQuery<PlaybookSuggestion[]>({
    queryKey: ['playbook', 'random', entryType, limit],
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/random?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch random suggestions');
      }
      return response.json();
    },
    staleTime: 60 * 1000, // 1 minute (want fresh random each time)
  });
}

/**
 * Log playbook entry usage for ML ranking
 */
export function useLogPlaybookUsage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      entry_id: string;
      action: 'viewed' | 'copied' | 'used' | 'dismissed';
      property_id?: number;
      context?: Record<string, unknown>;
    }) => {
      const response = await fetch(`${API_BASE}/usage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });
      if (!response.ok) {
        throw new Error('Failed to log usage');
      }
      return response.json();
    },
    onSuccess: () => {
      // Invalidate stats to reflect updated usage counts
      queryClient.invalidateQueries({ queryKey: ['playbook', 'stats'] });
    },
  });
}
